import { PartnerType } from "@/app/utils/partners.enum";
import { useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { CreditPartner } from "../../../useCreditController";
import { useSelfHiringContext } from "@/app/contexts/SelfHiringContext";
import { parse } from "date-fns";
import { toast } from "@/view/components/ui/use-toast";
import { useFGTSMutations } from "@/app/hooks/useFGTSMutations";
import { useParams } from "react-router-dom";
import { Table } from "@/app/entities/TableCredit";
import { useLeadMutations } from "@/app/hooks/useLeadMutations";
import { getProductsConfig } from "@/app/services/configService/getProductsConfig";
import { StatusEnum } from "@/app/utils/status";
import { useSelfHiringMutation } from "@/app/hooks/useSelfHiringMutation";
import { selfHireService } from "@/app/services/selfHireService";

export type OptionType = {
  type: CreditPartner;
  title: string;
  description: string;
  needAuthorization: boolean;
  submit?: () => void;
};

export const useSelectType = (
  methods: UseFormReturn<any>,
  partners: CreditPartner[],
) => {
  const { linkName } = useParams();
  const {
    setType,
    setStep2mode,
    setCrefazData,
    setFgtsData,
    setCltData,
    seller,
  } = useSelfHiringContext();

  const [isLoading, setIsLoading] = useState<
    Partial<Record<CreditPartner, boolean>>
  >({});
  const [isErrorOpen, setIsErrorOpen] = useState(false);

  // pan
  const [transaction, setTransaction] = useState<any | null>(null);
  const [simulatePanOpen, setSimulatePanOpen] = useState(false);

  const [isAuthorizationOpen, setIsAuthorizationOpen] = useState(false);
  const [isAuthorizationStepsOpen, setIsAuthorizationStepsOpen] =
    useState(false);
  const [authorizationContentType, setAuthorizationContentType] =
    useState<CreditPartner | null>(null);

  const { createLead } = useLeadMutations();
  const { simulateFGTSMutation } = useFGTSMutations();
  const {
    getPublicTransactionPanMutation,
    createCustomerMutation,
    makeProposalPanMutation,
    makeProposalCrefazMutation,
    simulateCltMutation,
  } = useSelfHiringMutation();

  const advanceFromAuthorizationModal = () => {
    setIsAuthorizationOpen(false);
    setIsAuthorizationStepsOpen(true);
  };

  const handleAuthorization = (open: boolean) => {
    setIsAuthorizationOpen(open);
  };

  const handleStepsAuthorizationOpenChange = (open: boolean) => {
    setIsAuthorizationStepsOpen(open);
    if (!open) {
      setAuthorizationContentType(null);
    }
  };

  const getCustomerData = () => {
    const data = methods.getValues() as any;

    const birthDate = parse(
      data.birthDate,
      "dd/MM/yyyy",
      new Date(),
    ).toISOString();
    return {
      name: data.name,
      cpf: data.cpf.replace(/\D/g, ""),
      birthDate,
      phone: data.phone.replace(/\D/g, ""),
      address: {
        zipCode: data.zipCode?.replace(/\D/g, ""),
        state: "",
        city: "",
        neighborhood: "",
        street: "",
        number: "",
        complement: "",
      },
    };
  };

  const handleSimulateCrefaz = async () => {
    setIsLoading((prev) => ({ ...prev, [PartnerType.CREFAZ]: true }));
    setType(PartnerType.CREFAZ);
    setAuthorizationContentType(null);

    try {
      const payload = getCustomerData();

      const proposal = await makeProposalCrefazMutation.mutateAsync({
        linkName: linkName || "",
        nome: payload.name,
        cpf: payload.cpf,
        nascimento: payload.birthDate,
        telefone: payload.phone,
        ocupacaoId: 1,
        cep: payload.address?.zipCode,
        city: payload.address?.city,
        state: payload.address?.state,
      });

      if (proposal?.propostaId) {
        setCrefazData({
          proposalData: { propostaId: proposal.propostaId },
          offersData: {
            offers: proposal.offers,
            proposta: proposal.proposta,
          },
        });
        setStep2mode("selectValues");
      }
    } catch (error: any) {
      toast({
        title: "Erro ao simular proposta",
        description:
          error?.message || "Erro inesperado. Tente novamente mais tarde.",
        variant: "destructive",
      });
    } finally {
      setIsLoading((prev) => ({ ...prev, [PartnerType.CREFAZ]: false }));
    }
  };

  const verifyPAN = async () => {
    const transactionId = transaction?._id ?? transaction?.id;
    if (!transactionId) return false;

    try {
      const tx = await selfHireService.getPublicTransactionPan({
        id: String(transactionId),
      });
      if (tx.status === StatusEnum.ACTION_REQUIRED) {
        setIsAuthorizationStepsOpen(false);
        setSimulatePanOpen(true);
        return true;
      }
      return false;
    } catch (error) {
      console.error(error);
      return false;
    }
  };

  const handleSimulatePan = async () => {
    setIsLoading((prev) => ({ ...prev, [PartnerType.PAN]: true }));
    setType(PartnerType.PAN);
    setAuthorizationContentType(PartnerType.PAN);
    try {
      const payload = getCustomerData();
      let tx;
      tx = await getPublicTransactionPanMutation.mutateAsync({
        customerCpf: payload.cpf,
        sellerId: seller?._id || "",
      });
      if (tx) {
        setTransaction(tx);
      } else {
        const customer = await createCustomerMutation.mutateAsync({
          name: payload.name,
          cpf: payload.cpf,
          phone: payload.phone,
          birthDate: payload.birthDate,
          email: "mock@email.com",
        });

        const proposal = await makeProposalPanMutation.mutateAsync({
          customerId: customer._id,
          linkName: linkName || "",
          type: PartnerType.PAN,
          providerData: {},
        });

        tx = await getPublicTransactionPanMutation.mutateAsync({
          id: proposal._id,
        });

        setTransaction(tx);
      }

      if (tx.url) {
        setIsAuthorizationOpen(true);
      } else {
        setAuthorizationContentType(null);
        toast({
          title: "Erro ao obter o link da proposta",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error(error);
      setAuthorizationContentType(null);
    } finally {
      setIsLoading((prev) => ({ ...prev, [PartnerType.PAN]: false }));
    }
  };

  const verifyAuthorization = async (
    type: PartnerType.FGTS | PartnerType.PAN,
  ) => {
    try {
      if (type === PartnerType.FGTS) {
        return await verifyFGTS();
      } else {
        return await verifyPAN();
      }
    } catch (error) {
      console.error(`erro ao verificar autorização ${type}`, error);
      return false;
    }
  };

  const verifyFGTS = async () => {
    try {
      const payload = getCustomerData();

      const lead = await createLead({
        ...payload,
        linkName: linkName || "",
      });

      const productsConfig = await getProductsConfig();
      if (!productsConfig.FGTS) {
        throw new Error("FGTS desativado");
      }

      const response = await simulateFGTSMutation.mutateAsync({
        cpf: payload.cpf,
        birthDate: payload.birthDate,
        linkName: linkName || "",
        leadId: lead._id,
      });

      const validTable = response.tables.find(
        (table) => table.success === true && table.ValorLiquidoCliente > 0,
      );

      if (!validTable) return false;

      setFgtsData({
        leadId: lead._id,
        table: validTable as unknown as Table,
      });

      setStep2mode("installments");
      return true;
    } catch (error) {
      console.error(error);
      return false;
    }
  };

  const handleSelectedFGTS = () => {
    setType(PartnerType.FGTS);
    setTransaction(null);
    setAuthorizationContentType(PartnerType.FGTS);
    setIsAuthorizationOpen(true);
  };

  const handleSimulateCLT = async () => {
    setIsLoading((prev) => ({ ...prev, [PartnerType.CLT]: true }));
    setType(PartnerType.CLT);
    setAuthorizationContentType(null);
    setStep2mode("installments");

    try {
      const payload = getCustomerData();

      const response = await simulateCltMutation.mutateAsync({
        cpf: payload.cpf,
        params: {
          linkName: linkName || "",
          birthDate: payload.birthDate,
        },
      });

      if (!response.available || !response.tables?.length) {
        toast({
          title: "Sem matrícula disponível",
          description: "Não localizamos uma matrícula elegível para esse CPF",
          variant: "destructive",
        });
        setStep2mode("partner");
        return;
      }

      setCltData({
        matricula: response.matricula,
        cnpjEmpresa: response.cnpjEmpresa,
        prazos: response.prazos,
        tables: response.tables,
        selectedPrazo: response.prazos?.[0] ?? response.tables[0]?.Prazo,
      });
    } catch (error: any) {
      toast({
        title: "Erro ao simular CLT",
        description:
          error?.message || "Erro inesperado. Tente novamente mais tarde.",
        variant: "destructive",
      });
      setStep2mode("partner");
    } finally {
      setIsLoading((prev) => ({ ...prev, [PartnerType.CLT]: false }));
    }
  };

  const allOptions: OptionType[] = [
    {
      type: PartnerType.FGTS,
      title: "FGTS - Saque Aniversário",
      description:
        "Antecipe parcelas do seu saque-aniversário do FGTS e receba o dinheiro de forma rápida na conta, com pagamento descontado diretamente do saldo do fundo.",
      needAuthorization: true,
      submit: () => handleSelectedFGTS(),
    },
    {
      type: PartnerType.PAN,
      title: "Empréstimo CLT",
      description:
        "Empréstimo consignado privado para trabalhadores de empresas conveniadas, com parcelas descontadas diretamente em folha e condições facilitadas.",
      needAuthorization: true,
      submit: () => handleSimulatePan(),
    },
    {
      type: PartnerType.CREFAZ,
      title: "Empréstimo na fatura de energia",
      description:
        "Crédito pessoal com parcelas incluídas diretamente na fatura de energia, oferecendo praticidade no pagamento e menos burocracia na contratação.",
      needAuthorization: false,
      submit: () => handleSimulateCrefaz(),
    },
    {
      type: PartnerType.CLT,
      title: "Empréstimo Consignado Privado",
      description:
        "Crédito com desconto em folha, aprovação facilitada e condições mais acessíveis.",
      needAuthorization: false,
      submit: () => handleSimulateCLT(),
    },
  ];

  const options = allOptions.filter((option) =>
    partners?.includes(option.type as CreditPartner),
  );

  return {
    options,
    linkPan: transaction?.url,
    authorizationContentType,
    isAuthorizationOpen,
    isAuthorizationStepsOpen,
    advanceFromAuthorizationModal,
    handleAuthorization,
    handleStepsAuthorizationOpenChange,
    isErrorOpen,
    setIsErrorOpen,
    verifyAuthorization,
    isLoading,
    simulatePanOpen,
    setSimulatePanOpen,
    transaction,
  };
};
